import React, { useState, useEffect } from "react";
import { Star, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Modal for a client to review a delivered order.
 * Fields: rating (1-5 stars), optional comment, optional quality complaint.
 */
export default function OrderReviewModal({ open, order, onClose, onSubmitted }) {
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [comment, setComment] = useState("");
    const [hasComplaint, setHasComplaint] = useState(false);
    const [complaintText, setComplaintText] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setRating(0);
            setHover(0);
            setComment("");
            setHasComplaint(false);
            setComplaintText("");
            setSubmitting(false);
        }
    }, [open]);

    if (!open || !order) return null;

    const submit = async () => {
        if (rating < 1) {
            toast.error("Merci d'attribuer au moins 1 étoile.");
            return;
        }
        if (hasComplaint && !complaintText.trim()) {
            toast.error("Merci de décrire le problème rencontré.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/orders/${order.id}/review`, {
                rating,
                comment: comment.trim() || null,
                has_complaint: hasComplaint,
                complaint_text: hasComplaint ? complaintText.trim() : null,
            });
            toast.success(
                hasComplaint
                    ? "Merci — votre réclamation a été transmise à l'équipe."
                    : "Merci pour votre évaluation !"
            );
            onSubmitted?.();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    const ratingLabels = ["", "Très déçu", "Décevant", "Correct", "Très bien", "Excellent"];
    const activeRating = hover || rating;

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)" }}
            data-testid="review-modal"
        >
            <div
                className="bc-card w-full max-w-lg max-h-[90vh] overflow-y-auto"
                style={{ padding: "2rem", background: "rgba(15,15,15,0.98)" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between">
                    <div>
                        <p className="bc-label">Évaluation de commande</p>
                        <h3 className="font-serif text-[#D4BC7C] text-xl mt-1">
                            #{order.id.slice(0, 8).toUpperCase()}
                        </h3>
                        <p className="text-xs text-[#A0A0A0] mt-1">
                            {order.items?.length || 0} article{(order.items?.length || 0) > 1 ? "s" : ""} · CHF {Number(order.total_ht || 0).toFixed(2)}
                        </p>
                    </div>
                    <button
                        className="text-[#A0A0A0] hover:text-[#D4BC7C]"
                        onClick={onClose}
                        data-testid="review-modal-close"
                        aria-label="Fermer"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="mt-6">
                    <p className="text-sm text-[#F2ECE0] mb-3">Comment évaluez-vous cette commande ?</p>
                    <div className="flex items-center gap-2" data-testid="review-stars">
                        {[1, 2, 3, 4, 5].map((n) => {
                            const filled = activeRating >= n;
                            return (
                                <button
                                    key={n}
                                    type="button"
                                    onMouseEnter={() => setHover(n)}
                                    onMouseLeave={() => setHover(0)}
                                    onClick={() => setRating(n)}
                                    className="transition-transform hover:scale-110"
                                    aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                                    data-testid={`review-star-${n}`}
                                >
                                    <Star
                                        size={32}
                                        className={filled ? "fill-[#D4BC7C] text-[#D4BC7C]" : "text-[#3a3a3a]"}
                                        strokeWidth={1.5}
                                    />
                                </button>
                            );
                        })}
                        {activeRating > 0 && (
                            <span className="ml-3 text-sm text-[#D4BC7C]" data-testid="review-rating-label">
                                {ratingLabels[activeRating]}
                            </span>
                        )}
                    </div>
                </div>

                <div className="mt-6">
                    <label className="bc-label">Commentaire (optionnel)</label>
                    <textarea
                        className="bc-input"
                        rows={3}
                        placeholder="Un mot pour féliciter l'équipe, ou une remarque…"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        data-testid="review-comment"
                    />
                </div>

                <div
                    className="mt-6 p-4 border"
                    style={{
                        borderColor: hasComplaint ? "#fca5a5" : "var(--bc-border)",
                        background: hasComplaint ? "rgba(252,165,165,0.05)" : "transparent",
                    }}
                >
                    <label className="flex items-start gap-3 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={hasComplaint}
                            onChange={(e) => setHasComplaint(e.target.checked)}
                            className="h-4 w-4 accent-[#fca5a5] mt-1"
                            data-testid="review-has-complaint"
                        />
                        <div className="flex-1">
                            <div className="flex items-center gap-2">
                                <AlertTriangle size={14} className="text-[#fca5a5]" />
                                <span className="text-sm text-[#F2ECE0] font-medium">
                                    Signaler un problème qualité
                                </span>
                            </div>
                            <p className="text-xs text-[#A0A0A0] mt-1">
                                Nous serons alertés immédiatement et vous contacterons sous 24h pour trouver une solution.
                            </p>
                        </div>
                    </label>

                    {hasComplaint && (
                        <div className="mt-4">
                            <label className="bc-label" style={{ color: "#fca5a5" }}>
                                Décrivez le problème rencontré
                            </label>
                            <textarea
                                className="bc-input"
                                rows={4}
                                placeholder="Ex : Le filet de bœuf présentait une couleur inhabituelle, non conforme à ce que nous recevons habituellement."
                                value={complaintText}
                                onChange={(e) => setComplaintText(e.target.value)}
                                style={{ borderColor: "#fca5a5" }}
                                data-testid="review-complaint-text"
                            />
                        </div>
                    )}
                </div>

                <div className="mt-8 flex gap-3 justify-end">
                    <button
                        className="bc-btn bc-btn--sm bc-btn--ghost"
                        onClick={onClose}
                        disabled={submitting}
                    >
                        Annuler
                    </button>
                    <button
                        className="bc-btn bc-btn--sm"
                        onClick={submit}
                        disabled={submitting || rating < 1}
                        data-testid="review-submit"
                    >
                        {submitting ? "Envoi…" : "Envoyer mon évaluation"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Small read-only view of an existing review (star row + labels).
 */
export function ReviewDisplay({ review, compact = false }) {
    if (!review) return null;
    const rating = Number(review.rating || 0);
    return (
        <div className="flex items-center gap-2" data-testid="review-display">
            <div className="flex items-center gap-0.5">
                {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                        key={n}
                        size={compact ? 12 : 14}
                        className={n <= rating ? "fill-[#D4BC7C] text-[#D4BC7C]" : "text-[#3a3a3a]"}
                        strokeWidth={1.5}
                    />
                ))}
            </div>
            <span className={`text-[#A0A0A0] ${compact ? "text-[10px]" : "text-xs"}`}>
                {rating}/5
                {review.has_complaint && (
                    <span className="ml-2 text-[#fca5a5] inline-flex items-center gap-0.5">
                        <AlertTriangle size={compact ? 9 : 11} /> Réclamation
                    </span>
                )}
            </span>
        </div>
    );
}
